/**
 * @fileoverview PWA Install Prompt Banner
 *
 * Shows an install banner for the MYNA AI tech portal when:
 * - The browser supports PWA installation (beforeinstallprompt event)
 * - The user hasn't dismissed it in the last 7 days
 * - The app is not already installed (standalone mode)
 *
 * Phase 12 of Production Excellence Plan v3.
 */

import { useState, useEffect, useCallback } from "react";
import { Download, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "pwa_install_dismissed_at";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Check if app is already installed (standalone mode)
  const isStandalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true);

  // Check if the user dismissed the prompt recently
  const wasDismissedRecently = useCallback(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (!dismissedAt) return false;
    return Date.now() - parseInt(dismissedAt, 10) < DISMISS_DURATION_MS;
  }, []);

  useEffect(() => {
    if (isStandalone || wasDismissedRecently()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone, wasDismissedRecently]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    setIsVisible(false);
    setDeferredPrompt(null);
  };

  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-r from-lime-500/10 to-teal-500/10 border border-lime-500/20 px-4 py-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Download className="w-5 h-5 text-lime-400 shrink-0" />
        <p className="text-sm text-gray-200 truncate">
          Install MYNA AI for faster access and offline support
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          onClick={handleInstall}
          size="sm"
          className="bg-lime-500 hover:bg-lime-400 text-black font-bold text-xs h-8"
        >
          Install
        </Button>
        <button
          onClick={handleDismiss}
          className="p-1 text-gray-400 hover:text-gray-200 transition-colors"
          aria-label="Dismiss install prompt"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
